import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';
import {
  Home,
  Users,
  Gift,
  Bell,
  Shield,
  CreditCard,
  LogOut,
} from 'lucide-react';
import { logout } from '../../redux/slices/authSlice';
import { PERMISSIONS, hasPermission } from '../../utils/permissions';
import { useModal } from '../../context/ModalContext';

const itemVariants = {
  initial: { rotateX: 0, opacity: 1 },
  hover: { rotateX: -90, opacity: 0 },
};

const backVariants = {
  initial: { rotateX: 90, opacity: 0 },
  hover: { rotateX: 0, opacity: 1 },
};

const glowVariants = {
  initial: { opacity: 0, scale: 0.8 },
  hover: {
    opacity: 1,
    scale: 2,
    transition: {
      opacity: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
      scale: { duration: 0.5, type: 'spring', stiffness: 300, damping: 25 },
    },
  },
};

const sharedTransition = {
  type: 'spring',
  stiffness: 100,
  damping: 20,
  duration: 0.5,
};

const menuItemsConfig = [
  {
    icon: <Home className="h-5 w-5" />,
    label: 'Dashboard',
    to: '/dashboard',
    gradient:
      'radial-gradient(circle, rgba(138,121,171,0.2) 0%, rgba(138,121,171,0.08) 50%, rgba(138,121,171,0) 100%)',
    iconColor: 'group-hover:text-primary',
    permission: PERMISSIONS.DASHBOARD_VIEW,
  },
  {
    icon: <CreditCard className="h-5 w-5" />,
    label: 'Ödemeler',
    to: '/odemeler',
    gradient:
      'radial-gradient(circle, rgba(0,184,122,0.2) 0%, rgba(0,184,122,0.08) 50%, rgba(0,184,122,0) 100%)',
    iconColor: 'group-hover:text-chart-2',
    permission: PERMISSIONS.PAYMENTS_VIEW,
  },
  {
    icon: <Users className="h-5 w-5" />,
    label: 'Müşteriler',
    to: '/kullanicilar',
    gradient:
      'radial-gradient(circle, rgba(119,184,161,0.2) 0%, rgba(119,184,161,0.08) 50%, rgba(119,184,161,0) 100%)',
    iconColor: 'group-hover:text-chart-3',
    permission: PERMISSIONS.USERS_READ,
  },
  {
    icon: <Gift className="h-5 w-5" />,
    label: 'Kampanyalar',
    to: '/kampanyalar/genel',
    gradient:
      'radial-gradient(circle, rgba(230,165,184,0.2) 0%, rgba(230,165,184,0.08) 50%, rgba(230,165,184,0) 100%)',
    iconColor: 'group-hover:text-chart-2',
    permission: PERMISSIONS.CAMPAIGNS_READ,
  },
  {
    icon: <Bell className="h-5 w-5" />,
    label: 'Bildirimler',
    to: '/bildirimler',
    gradient:
      'radial-gradient(circle, rgba(147,51,234,0.2) 0%, rgba(126,34,206,0.08) 50%, rgba(88,28,135,0) 100%)',
    iconColor: 'group-hover:text-purple-500',
    permission: PERMISSIONS.NOTIFICATIONS_READ,
  },
  {
    icon: <Shield className="h-5 w-5" />,
    label: 'Roller',
    to: '/kullanici-rolleri',
    gradient:
      'radial-gradient(circle, rgba(160,187,227,0.2) 0%, rgba(160,187,227,0.08) 50%, rgba(160,187,227,0) 100%)',
    iconColor: 'group-hover:text-chart-5',
    permission: PERMISSIONS.ROLES_READ,
  },
];

const MotionNavLink = motion.create(NavLink);

function NavBarItem({ item, disabled }) {
  const content = (
    <>
      <span
        className={cn(
          'transition-colors duration-300',
          disabled ? 'text-muted-foreground/50' : item.iconColor
        )}
      >
        {item.icon}
      </span>
      <span
        className={cn(
          'hidden md:inline font-medium',
          disabled ? 'text-muted-foreground/60' : undefined
        )}
      >
        {item.label}
      </span>
    </>
  );

  const linkClass =
    'flex flex-col md:flex-row items-center justify-center gap-0.5 md:gap-2 px-2 py-1.5 md:px-4 md:py-2 relative z-10 bg-transparent text-muted-foreground group-hover:text-foreground transition-colors rounded-xl md:rounded-2xl text-xs md:text-sm';

  return (
    <motion.li className="relative flex-1 md:flex-none">
      <motion.div
        className="block rounded-xl md:rounded-2xl overflow-visible group relative"
        style={{ perspective: '600px' }}
        initial="initial"
        animate="initial"
        whileHover={disabled ? undefined : 'hover'}
      >
        {!disabled && (
          <motion.div
            className="absolute inset-0 z-0 pointer-events-none rounded-xl md:rounded-2xl"
            variants={glowVariants}
            style={{ background: item.gradient, opacity: 0 }}
          />
        )}
        <MotionNavLink
          to={item.to}
          variants={disabled ? undefined : itemVariants}
          transition={sharedTransition}
          style={{ transformStyle: 'preserve-3d', transformOrigin: 'center bottom' }}
          className={({ isActive }) =>
            cn(
              linkClass,
              disabled
                ? 'opacity-40 cursor-not-allowed pointer-events-none'
                : isActive
                  ? 'text-primary font-medium'
                  : ''
            )
          }
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
        >
          {content}
        </MotionNavLink>
        {!disabled && (
          <MotionNavLink
            to={item.to}
            variants={backVariants}
            transition={sharedTransition}
            style={{
              position: 'absolute',
              inset: 0,
              transformStyle: 'preserve-3d',
              transformOrigin: 'center top',
              transform: 'rotateX(90deg)',
            }}
            className={({ isActive }) =>
              cn(linkClass, isActive ? 'text-primary font-medium' : '')
            }
          >
            {content}
          </MotionNavLink>
        )}
      </motion.div>
    </motion.li>
  );
}

export default function HoverGradientNavBar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { isAnyModalOpen } = useModal();

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate('/login', { replace: true });
    } catch {
      navigate('/login', { replace: true });
    }
  };

  const menuItems = menuItemsConfig.map((item) => ({
    ...item,
    disabled: user && item.permission && !hasPermission(user, item.permission),
  }));

  return (
    <div
      className={`fixed bottom-0 left-0 w-full md:bottom-4 md:left-1/2 md:-translate-x-1/2 z-50 transition-all duration-200 ${
        isAnyModalOpen ? 'opacity-0 pointer-events-none translate-y-4' : ''
      }`}
    >
      <motion.nav
        key={location.pathname}
        className="w-full md:w-fit mx-auto px-2 md:px-3 py-1.5 md:py-2 rounded-none md:rounded-2xl 
        bg-card/95 backdrop-blur-lg border border-border shadow-lg md:shadow-xl"
        initial="initial"
        animate="initial"
      >
        <ul className="flex items-center justify-around md:justify-center gap-1 md:gap-3 relative z-10">
          {menuItems.map((item) => (
            <NavBarItem key={item.label} item={item} disabled={item.disabled} />
          ))}
          <motion.li className="relative flex-1 md:flex-none">
            <motion.div
              className="block rounded-xl md:rounded-2xl overflow-visible group"
              style={{ perspective: '600px' }}
              initial="initial"
              animate="initial"
              whileHover="hover"
            >
              <motion.div
                className="absolute inset-0 z-0 pointer-events-none rounded-xl md:rounded-2xl"
                variants={glowVariants}
                style={{
                  background:
                    'radial-gradient(circle, rgba(217,95,92,0.2) 0%, rgba(217,95,92,0.08) 50%, rgba(217,95,92,0) 100%)',
                }}
              />
              <button
                type="button"
                onClick={handleLogout}
                className="flex flex-col md:flex-row items-center justify-center gap-0.5 md:gap-2 px-2 py-1.5 md:px-4 md:py-2 relative z-10 w-full bg-transparent text-muted-foreground hover:text-destructive transition-colors rounded-xl md:rounded-2xl text-xs md:text-sm"
              >
                <span className="transition-colors duration-300 group-hover:text-destructive">
                  <LogOut className="h-5 w-5" />
                </span>
                <span className="hidden md:inline font-medium">Çıkış</span>
              </button>
            </motion.div>
          </motion.li>
        </ul>
      </motion.nav>
    </div>
  );
}
